import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";
import Seo from "@/components/common/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { useRewardsMe, useRewardsStores } from "@/hooks/useRewards";
import { REWARDS } from "@/constants/testIds";
import BalanceCard from "@/components/rewards/BalanceCard";
import WelcomeBonusNudge from "@/components/rewards/WelcomeBonusNudge";
import StorePickerChips from "@/components/rewards/StorePickerChips";
import CheckinCard from "@/components/rewards/CheckinCard";
import ReviewProofCard from "@/components/rewards/ReviewProofCard";
import RedeemCard from "@/components/rewards/RedeemCard";
import ActiveCodesList from "@/components/rewards/ActiveCodesList";
import EventsLedger from "@/components/rewards/EventsLedger";
import GrantCelebrationBanner from "@/components/rewards/GrantCelebrationBanner";
import WalletTabs from "@/components/rewards/WalletTabs";
import ShareStreakCard from "@/components/rewards/ShareStreakCard";
import { Link, useSearchParams } from "react-router-dom";
import { Store, ShoppingBag, FileText, History } from "lucide-react";
import { usePointsBalance, usePointsTerms } from "@/hooks/usePoints";

/**
 * Rewards hub — the web mirror of the Expo `/rewards` screen. Layout:
 *   • Sticky-left balance card + activity ledger
 *   • Right column: store picker chips → check-in → receipt → review →
 *     redeem → redemption codes
 * Everything downstream of the store chips is scoped to the currently
 * selected store id.
 */
export default function RewardsHubPage() {
  const meQ = useRewardsMe();
  // Iter-600 §4 — coin-expiry notifications deep-link /rewards?redeem=N.
  const [urlParams] = useSearchParams();
  const redeemPrefill = Math.max(0, parseInt(urlParams.get("redeem"), 10) || 0);
  // Iter-352 — contextual nearest-store sorting: coords only exist after
  // the customer taps "Sort by nearest" and grants browser location.
  const [coords, setCoords] = useState(null);
  const [geoState, setGeoState] = useState("idle"); // idle | locating | active | denied
  const sortNearest = () => {
    if (!navigator.geolocation) {
      setGeoState("denied");
      return;
    }
    setGeoState("locating");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({
          lat: Number(pos.coords.latitude.toFixed(5)),
          lng: Number(pos.coords.longitude.toFixed(5)),
        });
        setGeoState("active");
      },
      () => setGeoState("denied"),
      { timeout: 10_000, maximumAge: 300_000 }
    );
  };
  const storesQ = useRewardsStores(coords);
  const stores = useMemo(() => storesQ.data || [], [storesQ.data]);
  const me = meQ.data || {};
  // Iter-395 workorder — wallets are SEPARATE. Every spendable number on
  // this NZ surface must be the NZ-stores wallet (`nz_classified_balance`
  // from GET /points/balance), never the combined rewards-me balance.
  const pointsQ = usePointsBalance();
  const balance = pointsQ.data?.nz_classified_balance ?? 0;
  // Iter-442+ — points economics are API-driven (GET /points/terms).
  const termsQ = usePointsTerms();
  const minRedeem = termsQ.data?.rates?.min_redeem_pts ?? 50;
  // Defensive sort: the backend order isn't a contract, and both the ledger
  // and the "most recent check-in" store-picker default rely on newest-
  // first. Sort here so both consumers see the same ordering.
  const events = useMemo(() => {
    const raw = me.events || me.ledger || [];
    return [...raw].sort((a, b) => {
      const ta = new Date(a?.created_at || 0).getTime() || 0;
      const tb = new Date(b?.created_at || 0).getTime() || 0;
      return tb - ta;
    });
  }, [me.events, me.ledger]);
  const codes = me.active_codes || me.codes || [];

  const [selectedStoreId, setSelectedStoreId] = useState(null);
  // First-time land: default the picker to the customer's most-recent
  // check-in store so the review/redeem cards are immediately
  // usable without an extra tap.
  useEffect(() => {
    if (selectedStoreId || !stores.length) return;
    const recent = events.find((e) => e.kind === "checkin" && e.store_id);
    setSelectedStoreId(recent?.store_id || stores[0].id);
  }, [stores, events, selectedStoreId]);

  const selectedStore = useMemo(
    () => stores.find((s) => s.id === selectedStoreId) || null,
    [stores, selectedStoreId]
  );

  // Iter 631 §1 — unseen goodwill grants → celebration banner above the
  // gold hero card. The banner marks each grant seen when displayed;
  // "Got it 🎉" removes it immediately.
  const grantsQ = useQuery({
    queryKey: ["grants-unseen"],
    retry: 0,
    queryFn: async () => (await sharedApi.get("/points/grants/unseen")).data,
  });
  const [dismissedGrants, setDismissedGrants] = useState([]);
  const unseenGrants = (grantsQ.data?.items || []).filter(
    (g) => !dismissedGrants.includes(g.id)
  );

  return (
    <div
      data-testid={REWARDS.root}
      className="max-w-6xl mx-auto px-4 md:px-8 py-8 md:py-12"
    >
      <Seo
        title="Allsale Rewards"
        description="Earn points at partner stores, redeem for discounts, and unlock counter codes on Allsale Rewards."
      />
      <Breadcrumbs items={[{ label: "Rewards" }]} />

      <div className="mt-4">
        <WelcomeBonusNudge />
      </div>

      <div className="mt-6 mb-6">
        <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900">
          Allsale Rewards
        </h1>
        <p className="mt-2 text-slate-500 max-w-2xl">
          Pay at partner stores and points land automatically — leave a review for bonus points,
          then redeem for real dollars off your next visit.
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1.5">
          <Link
            to="/rewards-terms"
            data-testid="rewards-terms-link"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[color:var(--brand-blue)] hover:underline"
          >
            <FileText className="w-3.5 h-3.5" /> How points work · Terms &amp; conditions
          </Link>
          {/* Iter-350 — founder-approved shortcut to the scan-&-save history */}
          <Link
            to="/my/visits"
            data-testid="rewards-my-visits-link"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[color:var(--brand-blue)] hover:underline"
          >
            <History className="w-3.5 h-3.5" /> My visits — your scan &amp; save history
          </Link>
        </div>
        {/* Iter 629 — wallet tabs replace the in-card "Buy points" CTA. */}
        <div className="mt-4">
          <WalletTabs active="coins" />
        </div>
      </div>

      <div className="grid lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)] gap-6">
        <div className="space-y-6 min-w-0">
          <GrantCelebrationBanner
            items={unseenGrants}
            onDismiss={(id) => setDismissedGrants((d) => [...d, id])}
          />
          {/* Iter-600 §4 — coin-expiry warning (fields from /points/balance) */}
          {Number(pointsQ.data?.nz_classified_expiring_soon) > 0 && (
            <div
              className="rounded-2xl bg-amber-50 border border-amber-300 p-4 text-sm font-semibold text-amber-800"
              data-testid="rewards-expiry-banner"
            >
              ⏳ {pointsQ.data.nz_classified_expiring_soon} coins expire
              {pointsQ.data.next_expiry_at
                ? ` by ${new Date(pointsQ.data.next_expiry_at).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" })}`
                : " soon"}{" "}
              — redeem them before they&apos;re gone!
            </div>
          )}
          <BalanceCard
            balance={balance}
            accountEmail={pointsQ.data?.account_email || me.account_email || ""}
            minRedeem={minRedeem}
            split={
              // Iter-622 fields — hide the split until the backend ships them.
              pointsQ.data?.nz_earned_balance !== undefined
                ? {
                    purchased: pointsQ.data?.nz_purchased_balance ?? 0,
                    purchasedExp: pointsQ.data?.nz_purchased_next_expiry_at || null,
                    earned: pointsQ.data?.nz_earned_balance ?? 0,
                    earnedExp: pointsQ.data?.nz_earned_next_expiry_at || null,
                  }
                : null
            }
          />
          <WalletSplit />
          <ShareStreakCard />
          <EventsLedger events={events} />
        </div>

        <div className="space-y-6 min-w-0">
          <StorePickerChips
            stores={stores}
            selectedId={selectedStoreId}
            onSelect={setSelectedStoreId}
            onSortNearest={sortNearest}
            geoState={geoState}
          />
          <CheckinCard />
          <div className="grid md:grid-cols-2 gap-6">
            {/* Iter-339 — receipt uploads retired: points auto-credit
                on partner-store payments (Iter-442+: flat 1 pt per visit). */}
            <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-5" data-testid="rewards-auto-earn-note">
              <div className="text-sm font-bold text-emerald-800">Points are automatic</div>
              <p className="mt-1.5 text-sm text-emerald-700 leading-relaxed">
                Points are added automatically when you pay at a partner store — a point every visit. No receipts needed!
              </p>
            </div>
            <ReviewProofCard storeId={selectedStoreId} store={selectedStore} />
          </div>
          <RedeemCard storeId={selectedStoreId} balance={balance} minRedeem={minRedeem} prefill={redeemPrefill} />
          <ActiveCodesList codes={codes} />
        </div>
      </div>

      <style>{`.input{width:100%;background:#fff;border:1px solid var(--border-soft);border-radius:14px;padding:.7rem 1rem;font-size:.95rem;color:var(--text-primary);}
      .input:focus{outline:none;border-color:var(--brand-blue);box-shadow:0 0 0 3px rgba(28,169,224,.12);}`}</style>
    </div>
  );
}

/* Iter-335 — the two wallets are SEPARATE: earn & spend stay inside the
   wallet they belong to. Balances come from GET /points/balance. */
function WalletSplit() {
  const q = usePointsBalance();
  const nz = q.data?.nz_classified_balance;
  const bazaar = q.data?.bazaar_balance;
  if (nz == null && bazaar == null) return null;
  return (
    <div className="rounded-2xl bg-white shadow-card p-4" data-testid="rewards-wallet-split">
      <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">Your wallets</div>
      <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm">
        <span className="inline-flex items-center gap-1.5 text-slate-700" data-testid="rewards-wallet-nz">
          <Store className="w-4 h-4 text-slate-400" /> NZ stores{" "}
          <span className="font-black text-slate-900">{nz ?? 0} pts</span>
        </span>
        <span className="inline-flex items-center gap-1.5 text-slate-700" data-testid="rewards-wallet-bazaar">
          <ShoppingBag className="w-4 h-4 text-slate-400" /> Bazaar{" "}
          <span className="font-black text-slate-900">{bazaar ?? 0} pts</span>
        </span>
      </div>
      <p className="mt-1.5 text-xs text-slate-400">
        Separate wallets — points are earned and spent within the same wallet.
      </p>
    </div>
  );
}
